/**
 * Outbound delivery statuses — what the Communication Center may
 * honestly claim about a text.
 *
 * Twilio creates a message as `queued` and reports every later step
 * (`sent`, `delivered`, `undelivered`, `failed`) only through a delivery
 * receipt. A row still in a pending status minutes later is not "being
 * sent": its receipt was never requested or was lost, and the backend
 * reconciles it with Twilio in the background. Until then the UI shows
 * a clock, not a spinner. `unknown` is what reconciliation records when
 * Twilio no longer knows the message at all.
 */

/** Statuses that still expect a delivery receipt. */
export const PENDING_DELIVERY_STATUSES: ReadonlyArray<string> = ['queued', 'sending', 'accepted'];

/**
 * Twilio's outbound statuses in the order a message passes through
 * them, plus `unknown` from reconciliation. Receipts do not arrive in
 * order — a late `sent` can follow `delivered` — so a status frame may
 * only move a message forward. Mirrors the backend's
 * DELIVERY_STATUS_RANK (sms_db_service).
 */
const DELIVERY_STATUS_RANK: Readonly<Record<string, number>> = {
    accepted: 0,
    scheduled: 0,
    queued: 1,
    sending: 2,
    sent: 3,
    unknown: 3,
    delivered: 4,
    undelivered: 4,
    failed: 4,
    canceled: 4,
    partially_delivered: 4,
    read: 5,
};

/** A status the table does not list is taken for a final outcome. */
const FINAL_STATUS_RANK = 4;

const deliveryStatusRank = (status: string): number =>
    DELIVERY_STATUS_RANK[status] ?? FINAL_STATUS_RANK;

/** Whether `next` may replace `current` without moving the message backwards. */
export const advancesDeliveryStatus = (current: string, next: string): boolean =>
    deliveryStatusRank(next) >= deliveryStatusRank(current);

/** Reconciliation found Twilio has no record of the message any more. */
export const UNKNOWN_DELIVERY_STATUS = 'unknown';

/** How long a pending message may legitimately be in flight. */
export const DELIVERY_RECEIPT_GRACE_MS = 2 * 60 * 1000;

export const NO_RECEIPT_LABEL = 'Sent — no delivery receipt';

export const isPendingDelivery = (status: string): boolean =>
    PENDING_DELIVERY_STATUSES.includes(status);

/** Pending, and Twilio has nothing more to say about it. */
export const isUnresolvedDelivery = (status: string): boolean =>
    isPendingDelivery(status) || status === UNKNOWN_DELIVERY_STATUS;

/**
 * True while a pending message is young enough that its receipt may
 * still be on the way. A message of unknown age cannot be called
 * "sending", so a missing or unreadable timestamp is not awaiting.
 */
export const isAwaitingReceipt = (
    status: string,
    sentAt: string | null | undefined,
    now: number = Date.now(),
): boolean => {
    if (!isPendingDelivery(status) || !sentAt) return false;
    const sentAtMs = new Date(sentAt).getTime();
    if (Number.isNaN(sentAtMs)) return false;
    return now - sentAtMs < DELIVERY_RECEIPT_GRACE_MS;
};
